import type { FC, ChangeEvent, FormEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../../../../../store/store';
import { setUser } from '../../../../../../../store/AuthSlice';
import { getCsrfToken } from '../../../../../../../utils/helpers';
import type { AddressStruct } from '../../../../../../../types/common';
import { AddressAny, buildFullAddress, debounce, emptyAddress, normalizeStreetType, shallowAddressFromLegacy } from '../address.helpers';
import type { UserProfile } from './userData.types';
import { coerceUserProfile, getTrimmedString, isRecord } from './userData.types';
import './deliveryAddressPopupContent.scss';

type Props = {
    user: UserProfile;
    onClose: () => void;
    mode: 'edit' | 'add';
    targetAddress: { id: string; raw: string; struct?: any | null; comment?: string; isPrimary: boolean } | null;
};
type SuggestScope = 'city' | 'street' | 'house';
type SuggestItem = {
    id: string;
    label: string;
    payload: unknown;
    scope: SuggestScope;
};
type DeliveryStatus = 'idle' | 'validating' | 'ok' | 'fail';

const SUGGEST_DEBOUNCE_MS = 300;
const DELIVERY_VALIDATE_DEBOUNCE_MS = 700;
const MAX_SUGGEST_CACHE = 120;
const MAX_COMMENT_LEN = 400;

const coerceInitialAddress = (user: UserProfile, mode: 'edit' | 'add', targetAddress: Props['targetAddress']): AddressAny => {
    if (mode === 'add') return { ...emptyAddress };
    const struct = targetAddress?.struct ?? (user as any)?.address?.struct;
    if (isRecord(struct)) {
        return { ...emptyAddress, ...(struct as unknown as AddressStruct) } as AddressAny;
    }
    const raw = getTrimmedString(targetAddress?.raw) || getTrimmedString((user as any)?.address?.raw);
    if (raw) {
        const parsed = shallowAddressFromLegacy(raw);
        return { ...emptyAddress, ...parsed };
    }
    return { ...emptyAddress };
};
const toOptionalNumber = (v: unknown): number | undefined => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
        const s = v.trim().replace(',', '.');
        if (!s) return undefined;
        const n = Number(s);
        if (Number.isFinite(n)) return n;
    }
    return undefined;
};
const stripControlChars = (s: string) => {
    let out = '';
    for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        if (code <= 31 || code === 127) continue;
        out += s[i];
    }
    return out;
};
const sanitizeCommentClient = (v: unknown): string =>
    stripControlChars(String(v ?? ''))
        .replace(/[<>]/g, '')
        .slice(0, MAX_COMMENT_LEN);

export const DeliveryAddressPopupContent: FC<Props> = ({ user, onClose, mode, targetAddress }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [addr, setAddr] = useState<AddressAny>(() => coerceInitialAddress(user, mode, targetAddress));
    const isPrimaryTarget = Boolean(targetAddress?.isPrimary);
    const additionalId = !isPrimaryTarget ? String(targetAddress?.id || '') : '';
    const isFirstAddressForUser = useMemo(() => {
        const hasPrimary = Boolean(getTrimmedString((user as any)?.address?.raw) || (user as any)?.address?.struct);
        const extras = Array.isArray((user as any)?.addresses) ? (user as any)?.addresses : [];
        const hasAnyExtraActive = extras.some((a: any) => a && !a.isDisabled && (getTrimmedString(a.raw) || a.struct));
        return !hasPrimary && !hasAnyExtraActive;
    }, [user]);
    const [makePrimary, setMakePrimary] = useState(() => (mode === 'add' && !isPrimaryTarget && isFirstAddressForUser ? true : false));
    const primaryComment = (user as any)?.address?.comment;
    const initialComment = useMemo(() => {
        if (isPrimaryTarget) return sanitizeCommentClient(primaryComment ?? '');
        return sanitizeCommentClient(targetAddress?.comment ?? '');
    }, [isPrimaryTarget, primaryComment, targetAddress?.comment]);
    const [commentDirty, setCommentDirty] = useState(false);
    const [comment, setComment] = useState(() => initialComment);
    const [addrErrors, setAddrErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [suggestions, setSuggestions] = useState<SuggestItem[]>([]);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [scope, setScope] = useState<SuggestScope | null>(null);
    const [highlight, setHighlight] = useState(-1);
    const [loadingSuggest, setLoadingSuggest] = useState(false);
    const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus>('idle');
    const [deliveryMsg, setDeliveryMsg] = useState('');
    const suggestAbortRef = useRef<AbortController | null>(null);
    const deliveryAbortRef = useRef<AbortController | null>(null);
    const postalRefineAbortRef = useRef<AbortController | null>(null);
    const saveAbortRef = useRef<AbortController | null>(null);
    const suggestCacheRef = useRef<Map<string, SuggestItem[]>>(new Map());
    const activeQueryKeyRef = useRef<string | null>(null);
    const prevDeliveryKeyRef = useRef<string>('');

    // const [indexOld, setIndexOld] = useState('');
    // const [indexRefined, setIndexRefined] = useState(false);
    const [userTouchedPostal, setUserTouchedPostal] = useState(false);
    const editKey = isPrimaryTarget ? 'primary' : `additional:${additionalId || 'new'}`;

    useEffect(() => {
        setAddr(coerceInitialAddress(user, mode, targetAddress));
        setMakePrimary(mode === 'add' && !isPrimaryTarget && isFirstAddressForUser ? true : false);
        setCommentDirty(false);
        setComment(initialComment);
        setAddrErrors({});
        setSaving(false);
        setSaveError('');
        setSuggestions([]);
        setSuggestOpen(false);
        setScope(null);
        setHighlight(-1);
        setLoadingSuggest(false);
        setDeliveryStatus('idle');
        setDeliveryMsg('');
        // setIndexOld('');
        // setIndexRefined(false);
        setUserTouchedPostal(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, mode, editKey, isFirstAddressForUser, initialComment]);

    useEffect(() => {
        const outside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[data-suggest-root="1"]')) setSuggestOpen(false);
        };
        document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, []);

    useEffect(() => {
        return () => {
            suggestAbortRef.current?.abort();
            deliveryAbortRef.current?.abort();
            postalRefineAbortRef.current?.abort();
            saveAbortRef.current?.abort();
        };
    }, []);

    const setAddrField = <K extends keyof AddressStruct>(key: K, value: AddressStruct[K]) => {
        setAddr((p) => ({ ...p, [key]: value }) as AddressAny);
        setAddrErrors((e) => {
            const copy = { ...e };
            delete copy[String(key)];
            delete copy.delivery;
            return copy;
        });
    };

    useEffect(() => {
        if (!commentDirty) setComment(initialComment);
    }, [initialComment, commentDirty]);

    const sufficientForDelivery = useCallback((): boolean => {
        const pcOk = /^\d{6}$/.test(String(addr.postalCode || ''));
        const hasIds = Boolean(addr.dpdCityId) && Boolean(addr.streetId);
        const hasHouse = Boolean(String(addr.house || '').trim());
        return pcOk && hasIds && hasHouse;
    }, [addr.postalCode, addr.dpdCityId, addr.streetId, addr.house]);

    const resetDelivery = () => {
        setDeliveryStatus('idle');
        setDeliveryMsg('');
        deliveryAbortRef.current?.abort();
    };

    const validateDelivery = useCallback(async () => {
        if (!sufficientForDelivery()) return;

        setDeliveryStatus('validating');
        setDeliveryMsg('Проверка доставки...');

        deliveryAbortRef.current?.abort();
        const ctrl = new AbortController();
        deliveryAbortRef.current = ctrl;

        try {
            const csrf = await getCsrfToken();
            const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/dpd/validate`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                body: JSON.stringify({ ...addr }),
                signal: ctrl.signal,
            });

            if (!r.ok) {
                const err = (await r.json().catch(() => ({ error: 'Адрес недоступен' }))) as unknown;
                const msg = typeof (err as { error?: unknown }).error === 'string' ? String((err as { error?: unknown }).error) : 'Адрес недоступен для доставки';
                setDeliveryStatus('fail');
                setDeliveryMsg(msg);
                return;
            }

            const data = (await r.json().catch(() => ({}))) as unknown;
            const deliverable = Boolean((data as { deliverable?: unknown }).deliverable);
            if (deliverable) {
                setDeliveryStatus('ok');
                setDeliveryMsg('Доставка DPD доступна');
            } else {
                const reason = typeof (data as { reason?: unknown }).reason === 'string' ? String((data as { reason?: unknown }).reason) : '';
                setDeliveryStatus('fail');
                setDeliveryMsg(reason || 'Адрес вне зоны доставки');
            }
        } catch (e: unknown) {
            const isAbort =
                typeof e === 'object' && e !== null && 'name' in e && typeof (e as { name?: unknown }).name === 'string'
                    ? (e as { name: string }).name === 'AbortError'
                    : false;
            if (isAbort) return;
            setDeliveryStatus('fail');
            setDeliveryMsg('Ошибка проверки доставки');
        }
    }, [addr, sufficientForDelivery]);

    const debouncedValidateDelivery = useMemo(() => debounce(() => void validateDelivery(), DELIVERY_VALIDATE_DEBOUNCE_MS), [validateDelivery]);

    const scheduleDeliveryValidation = () => {
        resetDelivery();
        if (!sufficientForDelivery()) return;
        debouncedValidateDelivery();
    };

    const deliveryKey = `${String(addr.dpdCityId ?? '')}|${String(addr.streetId ?? '')}|${String(addr.house ?? '')}|${String(addr.postalCode ?? '')}|${String(
        addr.block ?? '',
    )}`;

    useEffect(() => {
        if (prevDeliveryKeyRef.current !== deliveryKey) {
            scheduleDeliveryValidation();
            prevDeliveryKeyRef.current = deliveryKey;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryKey]);

    const refinePostal = useCallback(async () => {
        if (userTouchedPostal) return;
        if (!addr.city || !addr.street || !addr.house) return;

        const pieces = [`${addr.cityType || 'г.'} ${addr.city}`, `${normalizeStreetType(addr.streetType || 'ул.')} ${addr.street}`, String(addr.house)];
        if (addr.block) pieces.push(`к ${addr.block}`);

        const query = pieces.join(', ');
        if (query.length < 8) return;

        postalRefineAbortRef.current?.abort();
        const ctrl = new AbortController();
        postalRefineAbortRef.current = ctrl;

        try {
            const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/addr/refine?query=${encodeURIComponent(query)}`, {
                credentials: 'include',
                signal: ctrl.signal,
            });

            if (!r.ok) return;
            const data = (await r.json().catch(() => ({}))) as unknown;
            const pc = typeof (data as { postalCode?: unknown }).postalCode === 'string' ? String((data as { postalCode?: unknown }).postalCode) : '';

            if (pc && /^\d{6}$/.test(pc) && pc !== addr.postalCode) {
                // setIndexOld(String(addr.postalCode || ''));
                setAddrField('postalCode', pc);
                // setIndexRefined(true);
            }
        } catch (e: unknown) {
            const isAbort =
                typeof e === 'object' && e !== null && 'name' in e && typeof (e as { name?: unknown }).name === 'string'
                    ? (e as { name: string }).name === 'AbortError'
                    : false;
            if (isAbort) return;
        }
    }, [addr, userTouchedPostal]);

    const loadSuggest = useCallback(
        async (q: string, sc: SuggestScope) => {
            const query = q.trim();
            if (query.length < 2) {
                setSuggestions([]);
                setSuggestOpen(false);
                return;
            }

            const key = `${sc}:${String(addr.dpdCityId || 'x')}:${String(addr.streetId || 'x')}:${query}`;
            activeQueryKeyRef.current = key;

            if (suggestCacheRef.current.has(key)) {
                setSuggestions(suggestCacheRef.current.get(key) || []);
                setSuggestOpen(true);
                setHighlight(-1);
                return;
            }

            let url = '';
            if (sc === 'city') url = `${process.env.REACT_APP_SERVER_URL}/dpd/cities?q=${encodeURIComponent(query)}`;
            if (sc === 'street' && addr.dpdCityId)
                url = `${process.env.REACT_APP_SERVER_URL}/addr/streets?cityId=${encodeURIComponent(String(addr.dpdCityId))}&q=${encodeURIComponent(query)}`;
            if (sc === 'house' && addr.dpdCityId && addr.streetId)
                url = `${process.env.REACT_APP_SERVER_URL}/addr/houses?cityId=${encodeURIComponent(String(addr.dpdCityId))}&streetId=${encodeURIComponent(
                    String(addr.streetId),
                )}&q=${encodeURIComponent(query)}`;

            if (!url) return;

            suggestAbortRef.current?.abort();
            const ctrl = new AbortController();
            suggestAbortRef.current = ctrl;

            setLoadingSuggest(true);

            try {
                const r = await fetch(url, { credentials: 'include', signal: ctrl.signal });
                const json = (await r.json().catch(() => [])) as unknown;

                if (activeQueryKeyRef.current !== key) return;

                const list = Array.isArray(json) ? json : [];

                const mapped: SuggestItem[] = list.map((item) => {
                    if (!isRecord(item)) {
                        return { id: `${sc}-invalid`, label: '', payload: item, scope: sc };
                    }

                    if (sc === 'city') {
                        const city = getTrimmedString(item.city) || getTrimmedString(item.cityName) || getTrimmedString(item.name);
                        const region = getTrimmedString(item.region) || getTrimmedString(item.regionName);
                        const idRaw = item.dpdCityId ?? item.cityId ?? item.id ?? city;
                        const id = String(idRaw);
                        return {
                            id,
                            label: region ? `${city} (${region})` : city,
                            payload: item,
                            scope: sc,
                        };
                    }

                    if (sc === 'street') {
                        const stType = normalizeStreetType(getTrimmedString(item.streetType) || getTrimmedString(item.type) || 'ул.');
                        const stName = getTrimmedString(item.street) || getTrimmedString(item.name);
                        const id = String(item.fiasId ?? item.streetId ?? item.id ?? item.value ?? `${stType}-${stName}`);
                        return {
                            id,
                            label: `${stType} ${stName}`.trim(),
                            payload: item,
                            scope: sc,
                        };
                    }

                    const houseLabel = getTrimmedString(item.house) || getTrimmedString(item.value);
                    const id = String(item.house ?? item.value ?? houseLabel);
                    return { id, label: houseLabel, payload: item, scope: sc };
                });

                if (suggestCacheRef.current.size >= MAX_SUGGEST_CACHE) {
                    const it = suggestCacheRef.current.keys().next();
                    if (!it.done) suggestCacheRef.current.delete(it.value);
                }

                suggestCacheRef.current.set(
                    key,
                    mapped.filter((x) => x.label),
                );
                setSuggestions(mapped.filter((x) => x.label));
                setSuggestOpen(true);
                setHighlight(-1);
            } catch (e: unknown) {
                const isAbort =
                    typeof e === 'object' && e !== null && 'name' in e && typeof (e as { name?: unknown }).name === 'string'
                        ? (e as { name: string }).name === 'AbortError'
                        : false;
                if (!isAbort) {
                    setSuggestions([]);
                    setSuggestOpen(false);
                }
            } finally {
                setLoadingSuggest(false);
            }
        },
        [addr.dpdCityId, addr.streetId],
    );

    const debouncedSuggest = useMemo(() => debounce((q: string, sc: SuggestScope) => void loadSuggest(q, sc), SUGGEST_DEBOUNCE_MS), [loadSuggest]);

    const scheduleSuggest = (q: string, sc: SuggestScope) => {
        setScope(sc);
        debouncedSuggest(q, sc);
    };

    const chooseSuggest = (s: SuggestItem) => {
        if (!isRecord(s.payload)) return;

        if (s.scope === 'city') {
            const p = s.payload;

            // setIndexRefined(false);
            setUserTouchedPostal(false);

            const city = getTrimmedString(p.city) || getTrimmedString(p.cityName) || getTrimmedString(p.name);
            const region = getTrimmedString(p.region) || getTrimmedString(p.regionName);
            const cityType = getTrimmedString(p.cityType) || getTrimmedString(p.abbreviation) || 'г';
            const dpdCityId = (p.dpdCityId ?? p.cityId ?? p.id ?? null) as AddressStruct['dpdCityId'];
            const postalCode = getTrimmedString(p.postalCode) || getTrimmedString(p.index);

            const geoLat = toOptionalNumber(p.geoLat ?? p.lat);
            const geoLon = toOptionalNumber(p.geoLon ?? p.lon);

            setAddr(
                (prev): AddressAny => ({
                    ...prev,
                    postalCode: postalCode || prev.postalCode,
                    region: region || prev.region,
                    city,
                    cityType,
                    dpdCityId,
                    street: '',
                    streetType: '',
                    streetId: null,
                    house: '',
                    block: '',
                    flat: '',
                    geoLat,
                    geoLon,
                }),
            );

            resetDelivery();
        }

        if (s.scope === 'street') {
            const p = s.payload;

            // setIndexRefined(false);

            const street = getTrimmedString(p.street) || getTrimmedString(p.name);
            const streetType = normalizeStreetType(getTrimmedString(p.streetType) || getTrimmedString(p.type) || 'ул.');
            const streetId = (p.fiasId ?? p.streetId ?? p.id) as AddressStruct['streetId'];

            setAddr((prev) => ({
                ...prev,
                street,
                streetType,
                streetId,
                house: '',
                block: '',
                flat: '',
            }));

            resetDelivery();
            void refinePostal();
        }

        if (s.scope === 'house') {
            const p = s.payload;
            const house = getTrimmedString(p.house) || getTrimmedString(p.value);
            setAddrField('house', house as AddressStruct['house']);
            void refinePostal();
            scheduleDeliveryValidation();
        }

        setSuggestOpen(false);
        setSuggestions([]);
    };

    const handleSuggestKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!suggestOpen || !suggestions.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => (h + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter' && highlight >= 0) {
            e.preventDefault();
            chooseSuggest(suggestions[highlight]);
        } else if (e.key === 'Escape') {
            setSuggestOpen(false);
        }
    };

    const handleAddrChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        switch (name) {
            case 'postalCode':
                setUserTouchedPostal(true);
                // setIndexRefined(false);
                setAddrField('postalCode', value.replace(/\D/g, '').slice(0, 6) as AddressStruct['postalCode']);
                resetDelivery();
                break;

            case 'cityInput':
                setAddr((p) => ({
                    ...p,
                    city: value,
                    dpdCityId: null,
                    street: '',
                    streetId: null,
                    house: '',
                    block: '',
                    flat: '',
                }));
                setAddrErrors((e2) => {
                    const copy = { ...e2 };
                    delete copy.city;
                    delete copy.street;
                    delete copy.house;
                    delete copy.delivery;
                    delete copy.postalCode;
                    return copy;
                });
                setUserTouchedPostal(false);
                // setIndexRefined(false);
                scheduleSuggest(value, 'city');
                resetDelivery();
                break;

            case 'streetInput':
                setAddr((p) => ({
                    ...p,
                    street: value,
                    streetId: null,
                    house: '',
                    block: '',
                    flat: '',
                }));
                setAddrErrors((e2) => {
                    const copy = { ...e2 };
                    delete copy.street;
                    delete copy.house;
                    delete copy.delivery;
                    delete copy.postalCode;
                    return copy;
                });
                // setIndexRefined(false);
                if (addr.dpdCityId) scheduleSuggest(value, 'street');
                resetDelivery();
                break;

            case 'house':
                setAddrField('house', value.trim() as AddressStruct['house']);
                void refinePostal();
                break;

            case 'block':
                setAddrField('block', value.trim() as AddressStruct['block']);
                void refinePostal();
                break;

            case 'flat':
                setAddrField('flat', value.trim() as AddressStruct['flat']);
                break;

            default:
                break;
        }
    };

    const validateAll = (): boolean => {
        const ae: Record<string, string> = {};

        if (addr.postalCode && !/^\d{6}$/.test(String(addr.postalCode))) ae.postalCode = '6 цифр';
        if (!addr.dpdCityId) ae.city = 'Выберите город из списка';
        if (!addr.streetId) ae.street = 'Выберите улицу из списка';
        if (!String(addr.house || '').trim()) ae.house = 'Укажите дом';
        if (deliveryStatus === 'fail') ae.delivery = deliveryMsg || 'Доставка недоступна';

        setAddrErrors(ae);
        return Object.keys(ae).length === 0;
    };

    const handleSave = async (e: FormEvent) => {
        e.preventDefault();
        setSaveError('');

        if (!validateAll()) return;

        saveAbortRef.current?.abort();
        const ctrl = new AbortController();
        saveAbortRef.current = ctrl;

        setSaving(true);

        try {
            const csrfToken = await getCsrfToken();
            const addressRaw = buildFullAddress(addr);
            const hasMeaningfulAddress = Boolean(addr.dpdCityId || addr.city || addr.street || addr.house);
            if (!hasMeaningfulAddress) {
                setSaveError('Укажите адрес');
                return;
            }
            const safeComment = sanitizeCommentClient(comment);
            const payload = {
                mode, // 'add' | 'edit'
                target: isPrimaryTarget ? 'primary' : 'additional',
                addressId: !isPrimaryTarget ? additionalId : undefined,
                makePrimary: !isPrimaryTarget ? Boolean(makePrimary) : false,
                address: { raw: addressRaw, struct: addr, comment: safeComment },
            };

            const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/addresses/save`, {
                method: 'POST',
                credentials: 'include',
                signal: ctrl.signal,
                headers: { 'Content-Type': 'application/json', 'csrf-token': csrfToken },
                body: JSON.stringify(payload),
            });

            if (!r.ok) {
                const err = (await r.json().catch(() => null)) as unknown;
                const msg =
                    typeof (err as { error?: unknown }).error === 'string'
                        ? String((err as { error?: unknown }).error)
                        : typeof (err as { message?: unknown }).message === 'string'
                          ? String((err as { message?: unknown }).message)
                          : 'Не удалось сохранить изменения';
                setSaveError(msg);
                return;
            }

            const updated = (await r.json().catch(() => ({}))) as any;
            const nextUser = coerceUserProfile(updated);
            if (updated?.address && typeof updated.address === 'object') {
                (nextUser as any).address = {
                    ...(nextUser as any).address,
                    comment: typeof updated.address.comment === 'string' ? updated.address.comment : '',
                };
            }
            (nextUser as any).addresses = Array.isArray(updated?.addresses)
                ? updated.addresses.map((a: any) => ({
                      ...a,
                      comment: typeof a?.comment === 'string' ? a.comment : '',
                  }))
                : [];
            dispatch(setUser(nextUser));
            onClose();
        } catch (err: unknown) {
            const isAbort =
                typeof err === 'object' && err !== null && 'name' in err && typeof (err as { name?: unknown }).name === 'string'
                    ? (err as { name: string }).name === 'AbortError'
                    : false;
            if (!isAbort) setSaveError('Ошибка сети/сервера при сохранении');
        } finally {
            setSaving(false);
        }
    };

    const handleDisable = async () => {
        if (!additionalId) return;
        setSaveError('');
        saveAbortRef.current?.abort();
        const ctrl = new AbortController();
        saveAbortRef.current = ctrl;
        setSaving(true);
        try {
            const csrfToken = await getCsrfToken();
            const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/addresses/${encodeURIComponent(additionalId)}/disable`, {
                method: 'PATCH',
                credentials: 'include',
                signal: ctrl.signal,
                headers: { 'Content-Type': 'application/json', 'csrf-token': csrfToken },
                body: JSON.stringify({ isDisabled: true }),
            });
            if (!r.ok) {
                const err = (await r.json().catch(() => null)) as any;
                const msg = typeof err?.message === 'string' ? err.message : 'Не удалось удалить адрес';
                setSaveError(msg);
                return;
            }
            const updated = (await r.json().catch(() => ({}))) as any;
            const nextUser = coerceUserProfile(updated);
            if (updated?.address && typeof updated.address === 'object') {
                (nextUser as any).address = {
                    ...(nextUser as any).address,
                    comment: typeof updated.address.comment === 'string' ? updated.address.comment : '',
                };
            }
            (nextUser as any).addresses = Array.isArray(updated?.addresses)
                ? updated.addresses.map((a: any) => ({
                      ...a,
                      comment: typeof a?.comment === 'string' ? a.comment : '',
                  }))
                : [];
            dispatch(setUser(nextUser));
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <form className="deliveryAddressPopup" onSubmit={handleSave} noValidate>
            <div className="deliveryAddressPopup__head">
                <h1 className="deliveryAddressPopup__title">Адрес доставки</h1>
                <button className="deliveryAddressPopup__close" type="button" onClick={onClose} aria-label="Закрыть">
                    <img className="deliveryAddressPopup__closeIcon" src="/icons/new/close.svg" alt="" aria-hidden="true" />
                </button>
            </div>

            <p className="deliveryAddressPopup__subtitle">Введите адрес для доставки ваших покупок</p>

            <div className="deliveryAddressPopup__fields">
                <div className="deliveryAddressPopup__field" data-suggest-root="1">
                    <label className="deliveryAddressPopup__label" htmlFor="dap-city">
                        Город
                    </label>
                    <input
                        id="dap-city"
                        name="cityInput"
                        value={addr.city || ''}
                        onChange={handleAddrChange}
                        onKeyDown={handleSuggestKey}
                        onFocus={() => (addr.city ? scheduleSuggest(addr.city, 'city') : undefined)}
                        autoComplete="off"
                        className={`deliveryAddressPopup__input${addrErrors.city ? ' deliveryAddressPopup__input_error' : ''}`}
                        placeholder="Город*"
                        aria-autocomplete="list"
                        aria-invalid={addrErrors.city ? true : undefined}
                    />

                    {loadingSuggest && scope === 'city' ? <span className="deliveryAddressPopup__loader">…</span> : null}

                    {scope === 'city' && suggestOpen && suggestions.length > 0 ? (
                        <div className="deliveryAddressPopup__suggest" role="listbox" aria-label="Подсказки города">
                            {suggestions.map((s, i) => (
                                <div
                                    key={s.id}
                                    role="option"
                                    aria-selected={i === highlight}
                                    className={`deliveryAddressPopup__suggestItem${i === highlight ? ' deliveryAddressPopup__suggestItem_active' : ''}`}
                                    onMouseDown={() => chooseSuggest(s)}
                                >
                                    {s.label}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {addrErrors.city ? <div className="deliveryAddressPopup__error">{addrErrors.city}</div> : null}
                </div>

                <div className="deliveryAddressPopup__field" data-suggest-root="1">
                    <label className="deliveryAddressPopup__label" htmlFor="dap-street">
                        Улица
                    </label>
                    <input
                        id="dap-street"
                        name="streetInput"
                        value={addr.street || ''}
                        onChange={handleAddrChange}
                        onKeyDown={handleSuggestKey}
                        onFocus={() => (addr.street && addr.dpdCityId ? scheduleSuggest(addr.street, 'street') : undefined)}
                        autoComplete="off"
                        disabled={!addr.dpdCityId}
                        className={`deliveryAddressPopup__input${addrErrors.street ? ' deliveryAddressPopup__input_error' : ''}${
                            !addr.dpdCityId ? ' deliveryAddressPopup__input_disabled' : ''
                        }`}
                        placeholder="Улица*"
                        aria-autocomplete="list"
                        aria-invalid={addrErrors.street ? true : undefined}
                    />

                    {loadingSuggest && scope === 'street' ? <span className="deliveryAddressPopup__loader">…</span> : null}

                    {scope === 'street' && suggestOpen && suggestions.length > 0 ? (
                        <div className="deliveryAddressPopup__suggest" role="listbox" aria-label="Подсказки улицы">
                            {suggestions.map((s, i) => (
                                <div
                                    key={s.id}
                                    role="option"
                                    aria-selected={i === highlight}
                                    className={`deliveryAddressPopup__suggestItem${i === highlight ? ' deliveryAddressPopup__suggestItem_active' : ''}`}
                                    onMouseDown={() => chooseSuggest(s)}
                                >
                                    {s.label}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {addrErrors.street ? <div className="deliveryAddressPopup__error">{addrErrors.street}</div> : null}
                </div>

                <div className="deliveryAddressPopup__row3">
                    <div className="deliveryAddressPopup__field">
                        <label className="deliveryAddressPopup__label" htmlFor="dap-house">
                            Дом
                        </label>
                        <input
                            id="dap-house"
                            name="house"
                            value={String(addr.house || '')}
                            onChange={handleAddrChange}
                            onBlur={scheduleDeliveryValidation}
                            className={`deliveryAddressPopup__input deliveryAddressPopup__input_small${addrErrors.house ? ' deliveryAddressPopup__input_error' : ''}`}
                            placeholder="Дом*"
                            aria-invalid={addrErrors.house ? true : undefined}
                        />
                        {addrErrors.house ? <div className="deliveryAddressPopup__error">{addrErrors.house}</div> : null}
                    </div>

                    <div className="deliveryAddressPopup__field">
                        <label className="deliveryAddressPopup__label" htmlFor="dap-block">
                            Корп./Стр.
                        </label>
                        <input
                            id="dap-block"
                            name="block"
                            value={String(addr.block || '')}
                            onChange={handleAddrChange}
                            className="deliveryAddressPopup__input deliveryAddressPopup__input_small"
                            placeholder="Корп/Стр"
                        />
                    </div>

                    <div className="deliveryAddressPopup__field">
                        <label className="deliveryAddressPopup__label" htmlFor="dap-flat">
                            Кв.
                        </label>
                        <input
                            id="dap-flat"
                            name="flat"
                            value={String(addr.flat || '')}
                            onChange={handleAddrChange}
                            className="deliveryAddressPopup__input deliveryAddressPopup__input_small"
                            placeholder="Кв"
                        />
                        {/* <div className="deliveryAddressPopup__errorSpacer" /> */}
                    </div>
                </div>

                <input name="postalCode" value={String(addr.postalCode || '')} onChange={handleAddrChange} style={{ display: 'none' }} />

                {!isPrimaryTarget ? (
                    <label className="deliveryAddressPopup__switchRow">
                        <input className="deliveryAddressPopup__switch" type="checkbox" checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} />
                        <span className="deliveryAddressPopup__switchUi" aria-hidden="true" />
                        <span className="deliveryAddressPopup__switchText">Сделать основным</span>
                    </label>
                ) : null}
                <div className="deliveryAddressPopup__field">
                    <label className="deliveryAddressPopup__label" htmlFor="dap-comment">
                        Комментарии
                    </label>
                    <textarea
                        id="dap-comment"
                        name="comment"
                        className="deliveryAddressPopup__textarea"
                        placeholder="Комментарии"
                        maxLength={80}
                        value={comment}
                        onChange={(e) => {
                            setCommentDirty(true);
                            setComment(sanitizeCommentClient(e.target.value));
                        }}
                        rows={6}
                    />

                    {/* {indexRefined ? (
                        <div className="deliveryAddressPopup__hintSmall" aria-live="polite">
                            Индекс уточнён{indexOld ? `: ${indexOld} → ${String(addr.postalCode || '')}` : ''}
                        </div>
                    ) : (
                        <div className="deliveryAddressPopup__hintSmallSpacer" />
                    )} */}

                    {deliveryStatus === 'validating' ? (
                        <div className="deliveryAddressPopup__delivery deliveryAddressPopup__delivery_muted">Проверка доставки…</div>
                    ) : null}
                    {deliveryStatus === 'ok' ? <div className="deliveryAddressPopup__delivery deliveryAddressPopup__delivery_ok">{deliveryMsg}</div> : null}
                    {deliveryStatus === 'fail' ? <div className="deliveryAddressPopup__delivery deliveryAddressPopup__delivery_bad">{deliveryMsg}</div> : null}
                    {addrErrors.delivery ? <div className="deliveryAddressPopup__delivery deliveryAddressPopup__delivery_bad">{addrErrors.delivery}</div> : null}
                </div>
            </div>
            <div className="deliveryAddressPopup__spacer" />
            {saveError ? (
                <div className="deliveryAddressPopup__status" role="alert" aria-live="polite">
                    {saveError}
                </div>
            ) : null}
            {!isPrimaryTarget && mode === 'edit' ? (
                <button type="button" className="deliveryAddressPopup__delete" onClick={handleDisable}>
                    Удалить адрес
                </button>
            ) : null}
            <button className="deliveryAddressPopup__submit" type="submit" disabled={saving || deliveryStatus === 'validating'} aria-disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
            </button>
        </form>
    );
};

export default DeliveryAddressPopupContent;
